/* mobile-nav.js — drawer controller for the SP/Tablet primary navigation.
 *
 * Public API:
 *   window.KoshoMobileNav.open()
 *   window.KoshoMobileNav.close()
 *   window.KoshoMobileNav.toggle()
 *   window.KoshoMobileNav.isOpen()
 *
 * Auto-binds to:
 *   [data-mobile-nav-trigger]  → toggle()
 *   [data-mobile-nav-close]    → close()
 *
 * Renders the drawer markup if not present in DOM. Mounts to #mobile-nav-root
 * if present, otherwise appends a new element to <body>.
 *
 * Animation: fade backdrop + slide drawer; CSS handles the transition timing
 * (var(--transition-base) ≈ 280ms ease-out — close enough to the spec
 * 300ms cubic-bezier(0.4, 0, 0.2, 1); CSS literal is not used per token rule).
 *
 * Depends on: focus-trap.js (loaded before this file).
 */

(function (global) {
  'use strict';

  var ROOT_SELECTOR = '#mobile-nav-root';
  var TRIGGER_SELECTOR = '[data-mobile-nav-trigger], [data-action="open-mobile-nav"]';
  var CLOSE_SELECTOR = '[data-mobile-nav-close]';

  // The drawer mirrors the DESKTOP primary nav 1:1 so the two never drift
  // (spec: モバイルのヘッダーリンクをPCと一致させる). Links are read LIVE from the
  // server-rendered header (.header__nav-list in sections/header.liquid) — the
  // single source of truth — so labels/hrefs always match PC exactly.
  // FALLBACK_LINKS is used only if that nav is somehow absent on the page; it
  // carries the same labels + Shopify routes (NOT the old broken *.html slugs).
  var FALLBACK_LINKS = [
    { label: 'Home',      href: '/' },
    { label: 'Products',  href: '/collections/all' },
    { label: 'Breweries', href: '/pages/breweries' },
    { label: 'Venues',    href: '/pages/venues' },
    { label: 'Journal',   href: '/blogs/journal' },
    { label: 'News',      href: '/blogs/news' },
    { label: 'About',     href: '/pages/about' }
  ];

  // Read the desktop nav links straight from the DOM so the drawer is always an
  // exact copy (label + href) of what the header renders on PC.
  function collectNavLinks() {
    var out = [];
    var nodes = document.querySelectorAll('.header__nav-list .header__nav-link');
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var label = (a.textContent || '').replace(/\s+/g, ' ').trim();
      var href = a.getAttribute('href') || '#';
      if (label) out.push({ label: label, href: href });
    }
    return out.length ? out : FALLBACK_LINKS;
  }

  var rootEl = null;       // .mobile-nav root
  var panelEl = null;      // .mobile-nav__panel
  var backdropEl = null;
  var triggerEl = null;    // remembered for focus restore
  var trap = null;
  var isOpen = false;

  function svgIconClose() {
    return '<svg viewBox="0 0 16 16" aria-hidden="true">' +
           '<path d="M3 3 L13 13 M13 3 L3 13"></path></svg>';
  }

  function buildList(items) {
    var html = '<ul class="mobile-nav__list">';
    for (var i = 0; i < items.length; i++) {
      html += '<li class="mobile-nav__item">' +
              '<a class="mobile-nav__link" href="' + items[i].href + '">' +
              items[i].label + '</a></li>';
    }
    html += '</ul>';
    return html;
  }

  function buildMarkup() {
    return '' +
      '<div class="mobile-nav__backdrop" data-mobile-nav-close aria-hidden="true"></div>' +
      '<aside class="mobile-nav__panel" role="dialog" aria-modal="true" aria-label="Site navigation">' +
        '<div class="mobile-nav__header">' +
          '<a class="mobile-nav__logo" href="/">KOSHO SG</a>' +
          '<button type="button" class="mobile-nav__close" data-mobile-nav-close aria-label="Close menu">' +
            svgIconClose() +
          '</button>' +
        '</div>' +
        '<div class="mobile-nav__body">' +
          '<nav class="mobile-nav__section" aria-label="Primary">' +
            buildList(collectNavLinks()) +
          '</nav>' +
        '</div>' +
      '</aside>';
  }

  function ensureMounted() {
    if (rootEl) return;
    var mount = document.querySelector(ROOT_SELECTOR);
    rootEl = document.createElement('div');
    rootEl.className = 'mobile-nav';
    rootEl.id = 'mobile-nav-drawer'; // matches the hamburger's aria-controls
    rootEl.setAttribute('hidden', '');
    rootEl.innerHTML = buildMarkup();
    if (mount) {
      mount.appendChild(rootEl);
    } else {
      document.body.appendChild(rootEl);
    }
    panelEl = rootEl.querySelector('.mobile-nav__panel');
    backdropEl = rootEl.querySelector('.mobile-nav__backdrop');
    bindInternal();
    if (global.KoshoFocusTrap) {
      trap = global.KoshoFocusTrap.create(panelEl);
    }
  }

  function bindInternal() {
    // Close buttons (× and backdrop) are wired through delegation below
    rootEl.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== rootEl) {
        if (t.matches && t.matches(CLOSE_SELECTOR)) {
          close();
          return;
        }
        t = t.parentNode;
      }
    });
  }

  function onEsc(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      close();
    }
  }

  function open() {
    ensureMounted();
    if (isOpen) return;
    isOpen = true;
    triggerEl = document.activeElement;
    rootEl.removeAttribute('hidden');
    // Force a reflow so the transition runs from initial state.
    /* eslint-disable-next-line no-unused-expressions */
    rootEl.offsetHeight;
    rootEl.classList.add('is-open');
    document.body.classList.add('no-scroll');
    document.addEventListener('keydown', onEsc);
    // Notify trigger
    var trigs = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < trigs.length; i++) {
      trigs[i].setAttribute('aria-expanded', 'true');
    }
    if (trap) trap.activate();
  }

  function close() {
    if (!isOpen || !rootEl) return;
    isOpen = false;
    rootEl.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    document.removeEventListener('keydown', onEsc);
    var trigs = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < trigs.length; i++) {
      trigs[i].setAttribute('aria-expanded', 'false');
    }
    if (trap) trap.deactivate();
    // After transition, hide entirely so it leaves the a11y tree.
    var dur = 320; // a touch over --transition-base
    setTimeout(function () {
      if (!isOpen && rootEl) rootEl.setAttribute('hidden', '');
    }, dur);
    // Restore focus to trigger
    if (triggerEl && typeof triggerEl.focus === 'function') {
      try { triggerEl.focus(); } catch (err) { /* ignore */ }
    }
  }

  function toggle() {
    if (isOpen) close(); else open();
  }

  function bindTriggers() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== document) {
        if (t.matches && t.matches(TRIGGER_SELECTOR)) {
          e.preventDefault();
          toggle();
          return;
        }
        t = t.parentNode;
      }
    });
  }

  function init() {
    ensureMounted();
    bindTriggers();
    // Initialize aria-expanded on triggers
    var trigs = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < trigs.length; i++) {
      if (!trigs[i].hasAttribute('aria-expanded')) {
        trigs[i].setAttribute('aria-expanded', 'false');
      }
    }
    // Auto-close drawer if viewport widens past tablet
    var mq = window.matchMedia('(min-width: 1280px)');
    var onMqChange = function (e) { if (e.matches) close(); };
    if (mq.addEventListener) {
      mq.addEventListener('change', onMqChange);
    } else if (mq.addListener) {
      mq.addListener(onMqChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.KoshoMobileNav = {
    open: open,
    close: close,
    toggle: toggle,
    isOpen: function () { return isOpen; }
  };
})(window);
